import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'store';
import {
	removeNewlyCreatedObs,
	setDeletedObstructionIDsBulk,
	setManualObs,
	setEditedObstruction,
	setDrawnObsExists,
	setSelectedObstructionId,
} from 'store/slices/ToolSlice';

export type ObstructionUndoAction =
  | {
      type: 'ADD';
      obstructionId: string;
    }
  | {
      type: 'DELETE';
      obstructionId: string;
      snapshot?: Obstruction;
      wasInProgress: boolean;
    }
	| {
	type: 'EDIT';
	obstructionId: string;
	before: Obstruction;
	after: Obstruction;
	};

interface ObstructionUndoState {
  stack: ObstructionUndoAction[];
	deletedManuallyAddedObstructionIDs: string[];
}

const initialState: ObstructionUndoState = {
	stack: [],
	deletedManuallyAddedObstructionIDs: [],
};

const ObstructionUndoSlice = createSlice({
	name: 'obstructionUndo',
	initialState,
	reducers: {
		pushUndoAction: (state, action: PayloadAction<ObstructionUndoAction>) => {
			state.stack.push(action.payload);
		},
		undoLastAction: (state) => {
			state.stack.pop();
		},
		resetUndoStack: (state) => {
			state.stack = [];
		},
		setManuallyDeletedObstructionIDs:  (state, action: PayloadAction<{obstructionID: string}>)=> {
			const currentDeletedObstructionIDs = [...state.deletedManuallyAddedObstructionIDs, action.payload.obstructionID];
			state.deletedManuallyAddedObstructionIDs = currentDeletedObstructionIDs;
		}
	},
});

export const {
	pushUndoAction,
	undoLastAction,
	resetUndoStack,
	setManuallyDeletedObstructionIDs,
} = ObstructionUndoSlice.actions;

export default ObstructionUndoSlice.reducer;

export const undoLastObstruction = createAsyncThunk<
  void,
  void,
  { state: RootState }
>(
	'obstructionUndo/undoLastObstruction',
	async (_, { dispatch, getState }) => {
		const state = getState();
		const stack = state.obstructionState.stack;
		const selectedObstructionId = state.toolNewPostions.data.selectedObstructionId;

		if (!stack.length) return;

		const lastAction = stack[stack.length - 1];
		const shouldClearSelection = !!selectedObstructionId && lastAction.type !== 'EDIT';
		console.log('Undoing action: ', lastAction);

		// ---------------- UNDO ADD ----------------
		if (lastAction.type === 'ADD') {
			const obsId = lastAction.obstructionId;

			dispatch(removeNewlyCreatedObs({ id: obsId }));
			dispatch(setManuallyDeletedObstructionIDs({ obstructionID: obsId }));
		}

		// ---------------- UNDO DELETE ----------------
		if (lastAction.type === 'DELETE') {
			const { obstructionId, snapshot, wasInProgress } = lastAction;
			if (wasInProgress) {
				// Restore to manualObs (in-progress)
				console.log('Restoring in progress deleted obs: ', snapshot, obstructionId);
				dispatch(
					setManualObs({
						newlyAddedObs: snapshot!,
					})
				);
			} else {
				// Restore finalized obs
				const deletedIds =
      state.toolNewPostions.data.deletedObstructionIDs;

				dispatch(
					setDeletedObstructionIDsBulk({
						obstructionIDs: deletedIds.filter(
							(id) => id !== obstructionId
						),
					})
				);
			}
		}

		if(lastAction.type === 'EDIT') {
			const { before } = lastAction;
			const manualObsPool = state.toolNewPostions.data.manualObs;

			// If it exists in the manual (newly added) pool, restore it there
			if (manualObsPool && manualObsPool[before.id]) {
				dispatch(
					setManualObs({
						newlyAddedObs: before,
					})
				);
			} else {
				// Otherwise, it was an edit to a finalized/saved obstruction
				dispatch(
					setEditedObstruction({
						id: before.id,
						coordinates: before.coordinates,
						assessment: before.assessment,
						userMapped: before.userMapped,
						area: before.area,
					})
				);
			}
		}

		dispatch(undoLastAction());
		console.log('stack: ', getState().obstructionState.stack);
		dispatch(setDrawnObsExists({ exists: getState().obstructionState.stack.length > 0 }));
		if (shouldClearSelection) {
			dispatch(setSelectedObstructionId({ id: null }));
		}
	}
);
